import React, { useState, useCallback } from "react";
import PageLayout from "../components/PageLayout";
import { theme } from "../theme";

const Card = ({ children, style = {} }) => (
  <div style={{
    background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`,
    borderRadius: 12, padding: 20, marginBottom: 20, ...style
  }}>{children}</div>
);

const ChartTitle = ({ children, color = theme.colors.accent }) => (
  <div style={{
    fontFamily: theme.fonts.display, fontSize: 12, fontWeight: 700,
    color, letterSpacing: 1, marginBottom: 14, textTransform: "uppercase"
  }}>{children}</div>
);

const RISK_COLOR = { critical: "#FF0033", high: "#FF3D57", medium: "#FFB800", low: "#00D4FF", clean: "#00E676", unknown: "#6B8DB0" };

const RiskBadge = ({ level }) => {
  const color = RISK_COLOR[level] || RISK_COLOR.unknown;
  return (
    <span style={{
      padding: "3px 10px", borderRadius: 4,
      background: `${color}22`, border: `1px solid ${color}55`,
      color, fontFamily: theme.fonts.mono, fontSize: 10, fontWeight: 700,
      textTransform: "uppercase", letterSpacing: 1
    }}>{level}</span>
  );
};

// Known malicious IP ranges / threat signatures (sample threat intel database)
const THREAT_DB = {
  "192.168.":    { risk: "low",      type: "Private Network",         tags: ["internal", "rfc1918"] },
  "10.":         { risk: "low",      type: "Private Network",         tags: ["internal", "rfc1918"] },
  "172.16.":     { risk: "low",      type: "Private Network",         tags: ["internal", "rfc1918"] },
  "127.":        { risk: "clean",    type: "Loopback",                tags: ["localhost"] },
  "0.0.0.0":     { risk: "unknown",  type: "Unspecified",             tags: [] },
  "255.255.255": { risk: "low",      type: "Broadcast",               tags: ["broadcast"] },
};

// Port threat signatures
const PORT_INTEL = {
  // ── Well-Known Ports (0–1023) ────────────────────────────────────────────────
  "0":    { service: "Reserved",        risk: "high",     note: "Should never appear in traffic — spoofed/malformed packet indicator" },
  "1":    { service: "TCPmux",          risk: "medium",   note: "Rarely used — suspicious if active" },
  "7":    { service: "Echo",            risk: "medium",   note: "Used in amplification DDoS attacks" },
  "9":    { service: "Discard/Wake-on-LAN", risk: "medium", note: "WoL exploitable on internal networks" },
  "13":   { service: "Daytime",         risk: "low",      note: "Legacy time protocol" },
  "19":   { service: "CHARGEN",         risk: "high",     note: "UDP amplification DDoS vector" },
  "20":   { service: "FTP-Data",        risk: "high",     note: "Unencrypted file transfer data channel" },
  "21":   { service: "FTP",             risk: "high",     note: "Brute force + plaintext credentials" },
  "22":   { service: "SSH",             risk: "medium",   note: "Brute force target — restrict to key auth" },
  "23":   { service: "Telnet",          risk: "critical", note: "Unencrypted — credentials sent in plaintext" },
  "25":   { service: "SMTP",            risk: "medium",   note: "Email relay — phishing/spam vector" },
  "37":   { service: "Time",            risk: "low",      note: "Legacy time protocol" },
  "42":   { service: "WINS",            risk: "medium",   note: "Windows name service — recon target" },
  "43":   { service: "WHOIS",           risk: "low",      note: "Recon/OSINT tool" },
  "49":   { service: "TACACS",          risk: "high",     note: "Authentication protocol — credential target" },
  "53":   { service: "DNS",             risk: "medium",   note: "DNS tunneling, cache poisoning, exfiltration" },
  "67":   { service: "DHCP Server",     risk: "high",     note: "DHCP starvation / rogue DHCP attacks" },
  "68":   { service: "DHCP Client",     risk: "medium",   note: "DHCP spoofing target" },
  "69":   { service: "TFTP",            risk: "high",     note: "No authentication — file exfiltration risk" },
  "70":   { service: "Gopher",          risk: "medium",   note: "Legacy protocol — rarely legitimate" },
  "79":   { service: "Finger",          risk: "medium",   note: "User enumeration — should be disabled" },
  "80":   { service: "HTTP",            risk: "medium",   note: "Web traffic — XSS, SQLi, CSRF attack surface" },
  "88":   { service: "Kerberos",        risk: "high",     note: "Kerberoasting, AS-REP roasting, Golden Ticket attacks" },
  "102":  { service: "S7/ISO-TSAP",     risk: "critical", note: "Siemens S7 PLC — ICS/SCADA attack vector" },
  "110":  { service: "POP3",            risk: "medium",   note: "Unencrypted email — credential exposure" },
  "111":  { service: "RPC/portmapper",  risk: "high",     note: "RPC enumeration — lateral movement enabler" },
  "119":  { service: "NNTP",            risk: "low",      note: "Legacy news protocol" },
  "123":  { service: "NTP",             risk: "medium",   note: "NTP amplification DDoS — monlist attack" },
  "135":  { service: "MS-RPC",          risk: "high",     note: "Windows RPC — EternalBlue / BlueKeep vector" },
  "137":  { service: "NetBIOS-NS",      risk: "high",     note: "NetBIOS name service — NBNS spoofing/poisoning" },
  "138":  { service: "NetBIOS-DGM",     risk: "high",     note: "NetBIOS datagram — recon and lateral movement" },
  "139":  { service: "NetBIOS-SSN",     risk: "high",     note: "SMB over NetBIOS — legacy exploitation target" },
  "143":  { service: "IMAP",            risk: "medium",   note: "Unencrypted email — brute force target" },
  "161":  { service: "SNMP",            risk: "high",     note: "Network discovery — default community strings" },
  "162":  { service: "SNMP Trap",       risk: "medium",   note: "SNMP notifications — info disclosure" },
  "177":  { service: "XDMCP",           risk: "high",     note: "Remote X display — unencrypted" },
  "179":  { service: "BGP",             risk: "high",     note: "BGP hijacking — internet routing attacks" },
  "194":  { service: "IRC",             risk: "high",     note: "Botnet C2 channel" },
  "220":  { service: "IMAP3",           risk: "medium",   note: "Legacy email protocol" },
  "264":  { service: "BGMP",            risk: "medium",   note: "Border Gateway Multicast Protocol" },
  "389":  { service: "LDAP",            risk: "high",     note: "LDAP injection, anonymous bind, credential harvesting" },
  "443":  { service: "HTTPS",           risk: "low",      note: "Encrypted web — SSL stripping possible" },
  "445":  { service: "SMB",             risk: "critical", note: "EternalBlue, WannaCry, NotPetya, ransomware vector" },
  "465":  { service: "SMTPS",           risk: "medium",   note: "Encrypted SMTP — phishing relay" },
  "500":  { service: "IKE/IPSec",       risk: "high",     note: "VPN — aggressive mode key exchange attacks" },
  "502":  { service: "Modbus",          risk: "critical", note: "ICS/SCADA — no auth, direct PLC control possible" },
  "512":  { service: "rexec",           risk: "critical", note: "Remote execution — no encryption" },
  "513":  { service: "rlogin",          risk: "critical", note: "Remote login — no encryption" },
  "514":  { service: "rsh/Syslog",      risk: "high",     note: "Remote shell without auth / log manipulation" },
  "515":  { service: "LPD",             risk: "medium",   note: "Printer daemon — info disclosure" },
  "520":  { service: "RIP",             risk: "high",     note: "Routing protocol — route poisoning attacks" },
  "554":  { service: "RTSP",            risk: "medium",   note: "Streaming — IP camera exploitation" },
  "587":  { service: "SMTP Submission", risk: "medium",   note: "Email submission — credential brute force" },
  "593":  { service: "HTTP RPC",        risk: "high",     note: "Windows RPC over HTTP" },
  "623":  { service: "IPMI/BMC",        risk: "critical", note: "Baseboard management — IPMI cipher 0 attack" },
  "631":  { service: "IPP",             risk: "medium",   note: "Printing — CUPS remote code execution" },
  "636":  { service: "LDAPS",           risk: "medium",   note: "Encrypted LDAP — still credential target" },
  "666":  { service: "Doom/Backdoor",   risk: "critical", note: "Known malware/backdoor port" },
  "691":  { service: "MS Exchange",     risk: "high",     note: "Exchange routing — RCE history" },
  "771":  { service: "RealPort",        risk: "medium",   note: "Serial device server" },
  "789":  { service: "Red Lion",        risk: "critical", note: "ICS/SCADA — Red Lion HMI" },
  "873":  { service: "rsync",           risk: "high",     note: "File sync — unauthenticated access if misconfigured" },
  "902":  { service: "VMware",          risk: "high",     note: "VMware ESXi — hypervisor attack surface" },
  "989":  { service: "FTPS-Data",       risk: "medium",   note: "Encrypted FTP data" },
  "990":  { service: "FTPS",            risk: "medium",   note: "Encrypted FTP control" },
  "993":  { service: "IMAPS",           risk: "low",      note: "Encrypted IMAP" },
  "995":  { service: "POP3S",           risk: "low",      note: "Encrypted POP3" },
  "1000": { service: "Cadlock",          risk: "low",      note: "Remote procedure call" },
  "1008": { service: "HTTP Alt",         risk: "medium",   note: "Alternate HTTP — check for tunneling" },
  "1023": { service: "Reserved",         risk: "medium",   note: "Last well-known port — often probed by scanners" },

  // ── Full Well-Known Port Coverage (filling gaps 0–1023) ───────────────────────
  "3":    { service: "Compression",      risk: "low",      note: "Legacy compression process — rarely seen" },
  "5":    { service: "RJE",              risk: "low",      note: "Remote Job Entry — legacy IBM" },
  "11":   { service: "SYSTAT",           risk: "medium",   note: "Active user enumeration — disable if present" },
  "15":   { service: "NETSTAT",          risk: "medium",   note: "Network statistics — info disclosure" },
  "17":   { service: "QOTD",             risk: "low",      note: "Quote of the Day — UDP amplification possible" },
  "18":   { service: "MSP",              risk: "low",      note: "Message Send Protocol" },
  "20":   { service: "FTP-Data",         risk: "high",     note: "FTP data channel — plaintext file transfers" },
  "24":   { service: "Private Mail",     risk: "low",      note: "Legacy private mail system" },
  "26":   { service: "RSFTP",            risk: "low",      note: "Simple FTP variant" },
  "31":   { service: "MSG Auth",         risk: "low",      note: "Legacy message auth" },
  "33":   { service: "DSP",              risk: "low",      note: "Display Support Protocol" },
  "35":   { service: "Private Printer",  risk: "low",      note: "Legacy printer protocol" },
  "38":   { service: "RAP",              risk: "low",      note: "Route Access Protocol" },
  "39":   { service: "RLP",              risk: "low",      note: "Resource Location Protocol" },
  "41":   { service: "Graphics",         risk: "low",      note: "Legacy graphics protocol" },
  "44":   { service: "MPM Flags",        risk: "low",      note: "Message Processing Module" },
  "45":   { service: "MPM",              risk: "low",      note: "Message Processing Module" },
  "46":   { service: "MPM-SND",          risk: "low",      note: "MPM default send" },
  "47":   { service: "NI FTP",           risk: "medium",   note: "NI FTP — legacy file transfer" },
  "48":   { service: "Digital Audit",    risk: "low",      note: "Digital Audit Daemon" },
  "50":   { service: "Re-mail-ck",       risk: "low",      note: "Remote mail checking" },
  "51":   { service: "LA Maint",         risk: "low",      note: "IMP Logical Address Maintenance" },
  "52":   { service: "XNS Time",         risk: "low",      note: "XNS Time Protocol" },
  "54":   { service: "XNS Clearinghouse",risk: "low",      note: "XNS Clearinghouse" },
  "55":   { service: "ISI Graphics",     risk: "low",      note: "ISI Graphics Language" },
  "56":   { service: "XNS Auth",         risk: "low",      note: "XNS Authentication" },
  "57":   { service: "Private Terminal", risk: "low",      note: "Private terminal access" },
  "58":   { service: "XNS Mail",         risk: "low",      note: "XNS Mail" },
  "59":   { service: "Private File",     risk: "low",      note: "Private file service" },
  "60":   { service: "Unassigned",       risk: "medium",   note: "Unassigned — traffic here is suspicious" },
  "61":   { service: "NI Mail",          risk: "low",      note: "NI Mail — legacy" },
  "62":   { service: "ACA Services",     risk: "low",      note: "ACA Services" },
  "63":   { service: "WHOIS++",          risk: "low",      note: "Extended WHOIS" },
  "64":   { service: "CI Objects",       risk: "low",      note: "CI Objects protocol" },
  "65":   { service: "TACACS-DS",        risk: "high",     note: "TACACS Database Service — auth protocol" },
  "66":   { service: "Oracle SQL*NET",   risk: "high",     note: "Oracle SQL*NET v1 — legacy database" },
  "71":   { service: "Netrjs-1",         risk: "low",      note: "Remote Job Service" },
  "72":   { service: "Netrjs-2",         risk: "low",      note: "Remote Job Service" },
  "73":   { service: "Netrjs-3",         risk: "low",      note: "Remote Job Service" },
  "74":   { service: "Netrjs-4",         risk: "low",      note: "Remote Job Service" },
  "75":   { service: "Private Dial",     risk: "low",      note: "Private dial-out service" },
  "76":   { service: "Distributed Proc", risk: "low",      note: "Distributed Processing" },
  "77":   { service: "Private RJE",      risk: "low",      note: "Private RJE service" },
  "78":   { service: "vettcp",           risk: "low",      note: "vetTCP" },
  "81":   { service: "HTTP Alt",         risk: "medium",   note: "Alternate HTTP — often used for web admin" },
  "82":   { service: "XFER",             risk: "low",      note: "XFER utility" },
  "83":   { service: "MIT ML Device",    risk: "low",      note: "MIT ML Device" },
  "84":   { service: "CTF",              risk: "low",      note: "Common Trace Facility" },
  "85":   { service: "MIT ML Device",    risk: "low",      note: "MIT ML Device" },
  "86":   { service: "Micro Focus",      risk: "low",      note: "Micro Focus Cobol" },
  "87":   { service: "Private Terminal", risk: "low",      note: "Private link terminal" },
  "89":   { service: "SU/MIT Telnet",    risk: "high",     note: "SU/MIT Telnet gateway — unencrypted" },
  "90":   { service: "DNSIX",            risk: "medium",   note: "DNSIX security protocol" },
  "91":   { service: "MIT Dover Spooler",risk: "low",      note: "MIT Dover Spooler" },
  "92":   { service: "NPP",              risk: "low",      note: "Network Printing Protocol" },
  "93":   { service: "DCP",              risk: "low",      note: "Device Control Protocol" },
  "94":   { service: "OBJCALL",          risk: "low",      note: "Tivoli Object Dispatcher" },
  "95":   { service: "SUPDUP",           risk: "medium",   note: "SUPDUP terminal — legacy unencrypted" },
  "96":   { service: "DIXIE",            risk: "low",      note: "DIXIE Protocol Specification" },
  "97":   { service: "Swift RVF",        risk: "low",      note: "Swift Remote Virtual File" },
  "98":   { service: "TAC News",         risk: "low",      note: "TAC News" },
  "99":   { service: "Metagram",         risk: "low",      note: "Metagram Relay" },
  "100":  { service: "NIC Host Name",    risk: "low",      note: "NIC Host Name Server" },
  "101":  { service: "NIC Host Name",    risk: "low",      note: "NIC Host Name Server" },
  "103":  { service: "Genesis Point",    risk: "low",      note: "Genesis Point-to-Point" },
  "104":  { service: "DICOM",            risk: "high",     note: "Medical imaging — PHI exposure risk" },
  "105":  { service: "CCSO Nameserver",  risk: "low",      note: "CCSO Nameserver" },
  "106":  { service: "3COM-TSMUX",       risk: "low",      note: "3COM-TSMUX" },
  "107":  { service: "Remote Telnet",    risk: "critical", note: "Remote Telnet service — unencrypted" },
  "108":  { service: "SNA Gateway",      risk: "high",     note: "SNA Gateway access server" },
  "109":  { service: "POP2",             risk: "medium",   note: "Post Office Protocol v2 — legacy" },
  "112":  { service: "McIDAS",           risk: "low",      note: "McIDAS Data Transmission" },
  "113":  { service: "Ident/Auth",       risk: "medium",   note: "User identification — info disclosure" },
  "114":  { service: "Audio News",       risk: "low",      note: "Audio News Multicast" },
  "115":  { service: "SFTP",             risk: "medium",   note: "Simple FTP — not SSH SFTP, legacy protocol" },
  "116":  { service: "ANSA Notify",      risk: "low",      note: "ANSA REX Notify" },
  "117":  { service: "UUCP Path",        risk: "low",      note: "UUCP Path Service" },
  "118":  { service: "SQL Services",     risk: "high",     note: "SQL Services — database access" },
  "120":  { service: "CFDPTKT",          risk: "low",      note: "CFDPTKT" },
  "121":  { service: "Erpc",             risk: "low",      note: "Encore Expedited RPC" },
  "122":  { service: "SMAKYNET",         risk: "low",      note: "SMAKYNET" },
  "124":  { service: "UNITARY",          risk: "low",      note: "Unitary Login" },
  "125":  { service: "LOCUS-MAP",        risk: "low",      note: "Locus PC-Interface" },
  "126":  { service: "NXEdit",           risk: "low",      note: "NXEdit" },
  "127":  { service: "LOCUS-CON",        risk: "low",      note: "Locus PC-Interface Conn Server" },
  "128":  { service: "GSS-XLICEN",       risk: "low",      note: "GSS X License Verification" },
  "129":  { service: "PWDGEN",           risk: "medium",   note: "Password Generator Protocol — disable" },
  "130":  { service: "CISCO FNAS",       risk: "high",     note: "Cisco FNATIVE / network auth service" },
  "131":  { service: "CISCO TNAS",       risk: "high",     note: "Cisco TNATIVE" },
  "132":  { service: "CISCO SYSMAINT",   risk: "high",     note: "Cisco system maintenance" },
  "133":  { service: "Statistics",       risk: "low",      note: "Statistics Service" },
  "134":  { service: "INGRES-NET",       risk: "medium",   note: "INGRES-NET Service" },
  "136":  { service: "Profile Namesys",  risk: "low",      note: "PROFILE Naming System" },
  "140":  { service: "EMFIS-DATA",       risk: "low",      note: "EMFIS Data Service" },
  "141":  { service: "EMFIS-CNTL",       risk: "low",      note: "EMFIS Control Service" },
  "142":  { service: "BL-IDM",           risk: "low",      note: "Britton-Lee IDM" },
  "144":  { service: "NeWS",             risk: "low",      note: "Network extensible Window System" },
  "145":  { service: "UAAC",             risk: "low",      note: "UAAC Protocol" },
  "146":  { service: "ISO-TP0",          risk: "low",      note: "ISO-IP0" },
  "147":  { service: "ISO-IP",           risk: "low",      note: "ISO-IP" },
  "148":  { service: "JARGON",           risk: "low",      note: "Jargon" },
  "149":  { service: "AED-512",          risk: "low",      note: "AED 512 Emulation Service" },
  "150":  { service: "SQL-NET",          risk: "high",     note: "SQL-NET — Oracle database access" },
  "151":  { service: "HEMS",             risk: "low",      note: "HEMS" },
  "152":  { service: "BFTP",             risk: "medium",   note: "Background File Transfer" },
  "153":  { service: "SGMP",             risk: "medium",   note: "SGMP — SNMP predecessor" },
  "154":  { service: "NETSC-PROD",       risk: "low",      note: "NETSC production" },
  "155":  { service: "NETSC-DEV",        risk: "low",      note: "NETSC development" },
  "156":  { service: "SQL Service",      risk: "high",     note: "SQL Service — database access" },
  "157":  { service: "KNET/VM",          risk: "low",      note: "KNET/VM Command/Message Protocol" },
  "158":  { service: "PCMail Server",    risk: "low",      note: "PCMail Server" },
  "159":  { service: "NSS-Routing",      risk: "low",      note: "NSS-Routing" },
  "160":  { service: "SGMP-TRAPS",       risk: "medium",   note: "SGMP-TRAPS" },
  "163":  { service: "CMIP/TCP Manager", risk: "low",      note: "CMIP/TCP Manager" },
  "164":  { service: "CMIP/TCP Agent",   risk: "low",      note: "CMIP/TCP Agent" },
  "165":  { service: "XNS Courier",      risk: "low",      note: "Xerox" },
  "166":  { service: "S-Net",            risk: "low",      note: "Sirius Systems" },
  "167":  { service: "NAMP",             risk: "low",      note: "NAMP" },
  "168":  { service: "RSVD",             risk: "low",      note: "RSVD" },
  "169":  { service: "SEND",             risk: "low",      note: "SEND" },
  "170":  { service: "Print-SRV",        risk: "medium",   note: "Network PostScript" },
  "171":  { service: "Multiplex",        risk: "low",      note: "Network Innovations Multiplex" },
  "172":  { service: "CL/1",             risk: "low",      note: "Network Innovations CL/1" },
  "173":  { service: "Xyplex",           risk: "low",      note: "Xyplex" },
  "174":  { service: "MAILQ",            risk: "medium",   note: "MAILQ — mail queue" },
  "175":  { service: "VMNET",            risk: "low",      note: "VMNET" },
  "176":  { service: "GENRAD-MUX",       risk: "low",      note: "GENRAD-MUX" },
  "178":  { service: "NextStep",         risk: "low",      note: "NextStep Window Server" },
  "180":  { service: "SAS",              risk: "medium",   note: "SAS analytics — data access" },
  "181":  { service: "Unify",            risk: "low",      note: "Unify" },
  "182":  { service: "Audit",            risk: "low",      note: "Unisys Audit SITP" },
  "183":  { service: "OCBinder",         risk: "low",      note: "OCBinder" },
  "184":  { service: "OCServer",         risk: "low",      note: "OCServer" },
  "185":  { service: "Remote-KIS",       risk: "low",      note: "Remote-KIS" },
  "186":  { service: "KIS",              risk: "low",      note: "KIS Protocol" },
  "187":  { service: "Application Info", risk: "low",      note: "Application Info" },
  "188":  { service: "Plus Five MUMPS",  risk: "low",      note: "Plus Five's MUMPS" },
  "189":  { service: "Queued File Trans",risk: "low",      note: "Queued File Transport" },
  "190":  { service: "GACP",             risk: "low",      note: "Gateway Access Control" },
  "191":  { service: "Prospero",         risk: "low",      note: "Prospero Directory Service" },
  "192":  { service: "OSU-NMS",          risk: "low",      note: "OSU Network Monitoring" },
  "193":  { service: "SRMP",             risk: "low",      note: "Spider Remote Monitor" },
  "195":  { service: "DNSIX-DMDP",       risk: "low",      note: "DNSIX Network Level Module Audit" },
  "196":  { service: "DNSIX-SSDT",       risk: "low",      note: "DNSIX Session Mgt Module Audit" },
  "197":  { service: "DLS",              risk: "low",      note: "Directory Location Service" },
  "198":  { service: "DLS-Mon",          risk: "low",      note: "Directory Location Service Monitor" },
  "199":  { service: "SMUX",             risk: "medium",   note: "SMUX — SNMP multiplexer" },
  "200":  { service: "IBM System Res",   risk: "low",      note: "IBM System Resource Controller" },
  "201":  { service: "AT-RTMP",          risk: "low",      note: "AppleTalk Routing Maintenance" },
  "202":  { service: "AT-NBP",           risk: "low",      note: "AppleTalk Name Binding" },
  "204":  { service: "AT-Echo",          risk: "low",      note: "AppleTalk Echo" },
  "206":  { service: "AT-ZIS",           risk: "low",      note: "AppleTalk Zone Info" },
  "209":  { service: "QMTP",             risk: "medium",   note: "Quick Mail Transfer Protocol" },
  "210":  { service: "Z39.50",           risk: "low",      note: "ANSI Z39.50" },
  "213":  { service: "IPX",              risk: "medium",   note: "IPX — Novell NetWare" },
  "218":  { service: "MPP",              risk: "low",      note: "Netix Message Posting Protocol" },
  "219":  { service: "UARPS",            risk: "low",      note: "Unisys ARPs" },
  "221":  { service: "FMLP",             risk: "low",      note: "Berkeley rlogind with SPX auth" },
  "222":  { service: "ILS",              risk: "low",      note: "Berkeley rshd with SPX auth" },
  "223":  { service: "Certificate Dist", risk: "low",      note: "Certificate Distribution Center" },
  "224":  { service: "Masqdialer",       risk: "low",      note: "masqdialer" },
  "242":  { service: "Direct",           risk: "low",      note: "Direct" },
  "243":  { service: "Survey",           risk: "low",      note: "Survey Measurement" },
  "245":  { service: "LINK",             risk: "low",      note: "LINK" },
  "246":  { service: "DSP3270",          risk: "low",      note: "Display Systems Protocol" },
  "247":  { service: "SUBNTBCST_TFTP",   risk: "medium",   note: "SUBNTBCST_TFTP" },
  "248":  { service: "bhfhs",            risk: "low",      note: "bhfhs" },
  "256":  { service: "RAP",              risk: "low",      note: "RAP" },
  "257":  { service: "SET",              risk: "low",      note: "Secure Electronic Transaction" },
  "259":  { service: "ESRO-GEN",         risk: "low",      note: "Efficient Short Remote Operations" },
  "260":  { service: "Openport",         risk: "low",      note: "Openport" },
  "261":  { service: "NSIIOPS",          risk: "low",      note: "IIOP over TLS/SSL" },
  "262":  { service: "Arcisdms",         risk: "low",      note: "Arcisdms" },
  "263":  { service: "HDAP",             risk: "low",      note: "HDAP" },
  "265":  { service: "X-Bone HTTP",      risk: "medium",   note: "X-Bone HTTP" },
  "266":  { service: "SCSI-ST",          risk: "medium",   note: "SCSI on ST" },
  "267":  { service: "Tobit DaVinci",    risk: "low",      note: "Tobit David Service Layer" },
  "268":  { service: "Tobit DaVinci",    risk: "low",      note: "Tobit David Replica" },
  "280":  { service: "http-mgmt",        risk: "medium",   note: "HTTP management" },
  "281":  { service: "Personal Link",    risk: "low",      note: "Personal Link" },
  "282":  { service: "Cable Port A/X",   risk: "low",      note: "Cable Port A/X" },
  "283":  { service: "Rescap",           risk: "low",      note: "rescap" },
  "284":  { service: "Corerjd",          risk: "low",      note: "corerjd" },
  "286":  { service: "FXP",              risk: "medium",   note: "FXP Protocol" },
  "288":  { service: "IATP",             risk: "low",      note: "IATP" },
  "289":  { service: "Synoptics",        risk: "low",      note: "Synoptics Port Broker" },
  "308":  { service: "Novell Netware",   risk: "medium",   note: "Novell Netware IP Access" },
  "311":  { service: "AppleShare IP",    risk: "medium",   note: "AppleShare IP WebAdmin" },
  "318":  { service: "PKIX TSP",         risk: "low",      note: "PKIX Time Stamp" },
  "319":  { service: "PTP Event",        risk: "low",      note: "Precision Time Protocol" },
  "320":  { service: "PTP General",      risk: "low",      note: "Precision Time Protocol General" },
  "321":  { service: "PIP",              risk: "low",      note: "PIP" },
  "323":  { service: "IMMP",             risk: "low",      note: "IMMP" },
  "333":  { service: "Texar",            risk: "low",      note: "Texar Security Port" },
  "344":  { service: "PDAP",             risk: "low",      note: "Prospero Data Access" },
  "345":  { service: "PAWSERV",          risk: "low",      note: "Perf Analysis Workbench" },
  "346":  { service: "ZSERV",            risk: "low",      note: "Zebra server" },
  "347":  { service: "FATSERV",          risk: "low",      note: "Fatmen Server" },
  "348":  { service: "CSI-SGWP",         risk: "low",      note: "Cabletron Management Protocol" },
  "349":  { service: "MFTP",             risk: "medium",   note: "mftp" },
  "350":  { service: "MATIP-Type-A",     risk: "low",      note: "MATIP Type A" },
  "351":  { service: "MATIP-Type-B",     risk: "low",      note: "MATIP Type B" },
  "353":  { service: "NDSAUTH",          risk: "medium",   note: "NDS Auth" },
  "354":  { service: "BH611",            risk: "low",      note: "bh611" },
  "355":  { service: "DATEX-ASN",        risk: "low",      note: "DATEX-ASN" },
  "356":  { service: "Cloanto",          risk: "low",      note: "Cloanto Net 1" },
  "357":  { service: "BHEVENT",          risk: "low",      note: "bhevent" },
  "358":  { service: "Shrinkwrap",       risk: "low",      note: "Shrinkwrap" },
  "359":  { service: "NSS",              risk: "medium",   note: "Network Security Scanner" },
  "360":  { service: "Scoi2odialog",     risk: "low",      note: "scoi2odialog" },
  "361":  { service: "Semantix",         risk: "low",      note: "Semantix" },
  "362":  { service: "SRS-Send",         risk: "low",      note: "SRS Send" },
  "363":  { service: "RSVP-Tunnel",      risk: "medium",   note: "RSVP Tunnel" },
  "364":  { service: "Aurora CMGR",      risk: "low",      note: "Aurora CMGR" },
  "365":  { service: "DTK",              risk: "high",     note: "Deception Tool Kit — honeypot detection" },
  "366":  { service: "ODMR",             risk: "medium",   note: "On-Demand Mail Relay" },
  "367":  { service: "MortgageWare",     risk: "low",      note: "MortgageWare" },
  "368":  { service: "QBikGDP",          risk: "low",      note: "QbikGDP" },
  "369":  { service: "RPC2portmap",      risk: "high",     note: "Coda file system portmapper" },
  "370":  { service: "Codaauth2",        risk: "high",     note: "Coda authentication server" },
  "371":  { service: "Clearcase",        risk: "high",     note: "IBM ClearCase version control" },
  "372":  { service: "ListProcessor",    risk: "medium",   note: "ListProcessor" },
  "373":  { service: "Legent-1",         risk: "low",      note: "Legent Corporation" },
  "374":  { service: "Legent-2",         risk: "low",      note: "Legent Corporation" },
  "375":  { service: "Hassle",           risk: "low",      note: "Hassle" },
  "376":  { service: "NIP",              risk: "low",      note: "Amiga Envoy Network Inquiry" },
  "377":  { service: "TNETOS",           risk: "low",      note: "NEC Corporation" },
  "378":  { service: "DSETOS",           risk: "low",      note: "NEC Corporation" },
  "379":  { service: "IS99C",            risk: "low",      note: "TIA/EIA/IS-99" },
  "380":  { service: "IS99S",            risk: "low",      note: "TIA/EIA/IS-99 Server" },
  "381":  { service: "HP Performance",   risk: "medium",   note: "HP performance data collector" },
  "382":  { service: "HP Performance",   risk: "medium",   note: "HP performance data managed node" },
  "383":  { service: "HP Performance",   risk: "medium",   note: "HP performance data alarm manager" },
  "384":  { service: "A Remote Network", risk: "low",      note: "A Remote Network Server System" },
  "385":  { service: "IBM App",          risk: "medium",   note: "IBM Application" },
  "386":  { service: "ASA Message",      risk: "low",      note: "ASA Message Router Object" },
  "387":  { service: "AURP",             risk: "medium",   note: "AppleTalk Update-Based Routing" },
  "388":  { service: "Unidata LDM",      risk: "medium",   note: "Unidata LDM" },
  "390":  { service: "UIS",              risk: "low",      note: "UIS" },
  "391":  { service: "SynOptics SNMP",   risk: "high",     note: "SynOptics SNMP relay" },
  "392":  { service: "SynOptics Port",   risk: "medium",   note: "SynOptics port broker" },
  "393":  { service: "Dis Internet",     risk: "low",      note: "Data Interpretation System" },
  "394":  { service: "EMBL Nucleic",     risk: "low",      note: "EMBL Nucleic Data Transfer" },
  "395":  { service: "NetScout",         risk: "medium",   note: "NetScout Control Protocol" },
  "396":  { service: "Novell Netware",   risk: "medium",   note: "Novell Netware over IP" },
  "397":  { service: "Multi Protocol",   risk: "low",      note: "Multi Protocol Trans. Network" },
  "398":  { service: "Kryptolan",        risk: "low",      note: "Kryptolan" },
  "399":  { service: "ISO-TSAP-C2",      risk: "low",      note: "ISO Transport Class 2" },
  "400":  { service: "Oracle Secure",    risk: "high",     note: "Oracle Secure Backup" },
  "401":  { service: "UPS",              risk: "medium",   note: "Uninterruptible Power Supply" },
  "402":  { service: "Genie Protocol",   risk: "low",      note: "Genie Protocol" },
  "403":  { service: "Decap",            risk: "low",      note: "decap" },
  "404":  { service: "Nced",             risk: "low",      note: "nced" },
  "405":  { service: "Ncld",             risk: "low",      note: "ncld" },
  "406":  { service: "IMSPda",           risk: "low",      note: "Interactive Mail Support" },
  "407":  { service: "Timbuktu",         risk: "high",     note: "Remote control — possible RCE" },
  "408":  { service: "PRM-SM",           risk: "low",      note: "Prospero Resource Manager" },
  "409":  { service: "PRM-NM",           risk: "low",      note: "Prospero Resource Manager Node" },
  "410":  { service: "DECladebug",       risk: "high",     note: "DECladebug Remote Debug Protocol" },
  "411":  { service: "Remote MT",        risk: "medium",   note: "Remote MT Protocol" },
  "412":  { service: "Trap",             risk: "medium",   note: "Trap Convention Port" },
  "413":  { service: "SMSP",             risk: "low",      note: "SMSP" },
  "414":  { service: "Infoseek",         risk: "low",      note: "Infoseek" },
  "415":  { service: "BNet",             risk: "low",      note: "BNet" },
  "416":  { service: "Silverplatter",    risk: "low",      note: "Silverplatter" },
  "417":  { service: "Onmux",            risk: "low",      note: "Onmux" },
  "418":  { service: "Hyper-G",          risk: "low",      note: "Hyper-G" },
  "419":  { service: "Ariel 1",          risk: "low",      note: "Ariel 1" },
  "420":  { service: "SMPTE",            risk: "low",      note: "SMPTE" },
  "421":  { service: "Ariel 2",          risk: "low",      note: "Ariel 2" },
  "422":  { service: "Ariel 3",          risk: "low",      note: "Ariel 3" },
  "423":  { service: "OCP",              risk: "low",      note: "IBM Operations Planning" },
  "424":  { service: "IBM OPC",          risk: "low",      note: "IBM Operations Planning" },
  "425":  { service: "ICAD-El",          risk: "low",      note: "ICAD" },
  "426":  { service: "smartsdp",         risk: "low",      note: "smartsdp" },
  "427":  { service: "svrloc",           risk: "medium",   note: "Server Location — service discovery" },
  "428":  { service: "OCS_CMU",          risk: "low",      note: "OCS_CMU" },
  "429":  { service: "OCS_AMU",          risk: "low",      note: "OCS_AMU" },
  "430":  { service: "UTMPSD",           risk: "low",      note: "UTMPSD" },
  "431":  { service: "UTMPCD",           risk: "low",      note: "UTMPCD" },
  "432":  { service: "IASD",             risk: "low",      note: "IASD" },
  "433":  { service: "NNSP",             risk: "low",      note: "NNSP" },
  "434":  { service: "MobileIP-MN",      risk: "medium",   note: "MobileIP-Agent" },
  "435":  { service: "MobileIP-Agent",   risk: "medium",   note: "MobileIP-MN" },
  "436":  { service: "DNA-CML",          risk: "low",      note: "DNA-CML" },
  "437":  { service: "COMSCM",           risk: "low",      note: "comscm" },
  "438":  { service: "DSFGW",            risk: "low",      note: "dsfgw" },
  "439":  { service: "DASP",             risk: "low",      note: "dasp" },
  "440":  { service: "SGCP",             risk: "low",      note: "sgcp" },
  "441":  { service: "DECVMS-SYSMGT",    risk: "medium",   note: "decvms-sysmgt" },
  "442":  { service: "CVC_HOSTD",        risk: "low",      note: "cvc_hostd" },
  "444":  { service: "SNPP",             risk: "medium",   note: "Simple Network Paging" },
  "446":  { service: "DDM-RDB",          risk: "high",     note: "DDM-Remote Relational Database" },
  "447":  { service: "DDM-DFM",          risk: "high",     note: "DDM-Distributed File Management" },
  "448":  { service: "DDM-SSL",          risk: "medium",   note: "DDM-Remote DB Access SSL" },
  "449":  { service: "AS-SERVERMAP",     risk: "low",      note: "AS Server Mapper" },
  "450":  { service: "TSERVER",          risk: "low",      note: "Computer Supported Telephony" },
  "451":  { service: "SFS-SMP-NET",      risk: "low",      note: "Cray Network Semaphore" },
  "452":  { service: "SFS-CONFIG",       risk: "low",      note: "Cray SFS config server" },
  "453":  { service: "CREATIVESERVER",   risk: "low",      note: "CreativeServer" },
  "454":  { service: "CONTENTSERVER",    risk: "low",      note: "ContentServer" },
  "455":  { service: "CREATIVEPARTNR",   risk: "low",      note: "CreativePartnr" },
  "456":  { service: "MACON-TCP",        risk: "low",      note: "macon-tcp" },
  "457":  { service: "SCOHELP",          risk: "low",      note: "scohelp" },
  "458":  { service: "AppleTalk Quasimodo",risk:"low",     note: "AppleTalk Quasimodo" },
  "459":  { service: "AppleTalk Trampolino",risk:"low",    note: "AppleTalk Trampolino" },
  "460":  { service: "SKRONK",           risk: "low",      note: "skronk" },
  "461":  { service: "DataRampSrvr",     risk: "low",      note: "DataRampSrvr" },
  "462":  { service: "DataRampSrvrSec",  risk: "low",      note: "DataRampSrvrSec" },
  "463":  { service: "ALPES",            risk: "low",      note: "alpes" },
  "464":  { service: "Kpasswd",          risk: "high",     note: "Kerberos password change — auth protocol" },
  "466":  { service: "DIGITAL-VRC",      risk: "low",      note: "digital-vrc" },
  "467":  { service: "Mylex-Mapd",       risk: "low",      note: "mylex-mapd" },
  "468":  { service: "Photuris",         risk: "low",      note: "proturis" },
  "469":  { service: "RCP",              risk: "medium",   note: "Radio Control Protocol" },
  "470":  { service: "SCX-Proxy",        risk: "medium",   note: "scx-proxy" },
  "471":  { service: "Mondex",           risk: "low",      note: "Mondex" },
  "472":  { service: "LJK-LOGIN",        risk: "low",      note: "ljk-login" },
  "473":  { service: "Hybrid-POP",       risk: "medium",   note: "hybrid-pop" },
  "474":  { service: "TN-TL-W2",         risk: "low",      note: "tn-tl-w2" },
  "475":  { service: "TCPNETHASPSRV",    risk: "low",      note: "tcpnethaspsrv" },
  "476":  { service: "TN-TL-FD1",        risk: "low",      note: "tn-tl-fd1" },
  "477":  { service: "SS7-GW",           risk: "high",     note: "SS7 Gateway — telecom signaling attacks" },
  "478":  { service: "SPSC",             risk: "low",      note: "spsc" },
  "479":  { service: "IAFServer",        risk: "low",      note: "iafserver" },
  "480":  { service: "IAFdbase",         risk: "low",      note: "iafdbase" },
  "481":  { service: "Ph",               risk: "low",      note: "Ph service" },
  "482":  { service: "Bgs-nsi",          risk: "low",      note: "bgs-nsi" },
  "483":  { service: "Ulpnet",           risk: "low",      note: "ulpnet" },
  "484":  { service: "Integra SME",      risk: "low",      note: "Integra Software Management" },
  "485":  { service: "Air Soft Power",   risk: "low",      note: "Air Soft Power Chute" },
  "486":  { service: "Mtn",              risk: "low",      note: "mtn" },
  "487":  { service: "SAFT",             risk: "low",      note: "SAFT Simple Asynch File Transfer" },
  "488":  { service: "GSS-HTTP",         risk: "low",      note: "gss-http" },
  "489":  { service: "Nest-Protocol",    risk: "low",      note: "nest-protocol" },
  "490":  { service: "Micom-PFS",        risk: "low",      note: "micom-pfs" },
  "491":  { service: "Go-Login",         risk: "medium",   note: "go-login" },
  "492":  { service: "TicF",             risk: "low",      note: "Transport Independent Convergence" },
  "493":  { service: "Retrospect",       risk: "medium",   note: "Retrospect backup — data access" },
  "494":  { service: "POV-Ray",          risk: "low",      note: "POV-Ray" },
  "495":  { service: "Intecourier",      risk: "low",      note: "intecourier" },
  "496":  { service: "PIM-RP-DISC",      risk: "medium",   note: "PIM-RP-DISC — multicast routing" },
  "497":  { service: "Retrospect",       risk: "medium",   note: "Retrospect" },
  "498":  { service: "Siam",             risk: "low",      note: "siam" },
  "499":  { service: "ISO ILL",          risk: "low",      note: "ISO ILL Protocol" },
  "501":  { service: "STMF",             risk: "low",      note: "STMF" },
  "503":  { service: "Intrinsa",         risk: "low",      note: "Intrinsa" },
  "504":  { service: "Citadel",          risk: "medium",   note: "Citadel" },
  "505":  { service: "MAILBOX-LM",       risk: "medium",   note: "mailbox-lm" },
  "506":  { service: "OHIMSRV",          risk: "low",      note: "ohimsrv" },
  "507":  { service: "CRS",              risk: "low",      note: "crs" },
  "508":  { service: "XVD",              risk: "low",      note: "xvd" },
  "509":  { service: "Snare",            risk: "medium",   note: "snare" },
  "510":  { service: "FCP",              risk: "medium",   note: "FirstClass Protocol" },
  "511":  { service: "MyNet-AS",         risk: "low",      note: "mynet-as" },
  "516":  { service: "PDL",              risk: "low",      note: "PDL Data Delivery Protocol" },
  "517":  { service: "Talk",             risk: "medium",   note: "Talk — legacy chat, user enumeration" },
  "518":  { service: "NTalk",            risk: "medium",   note: "NTalk" },
  "519":  { service: "UTime",            risk: "low",      note: "unixtime" },
  "521":  { service: "RIPng",            risk: "high",     note: "RIPng — IPv6 routing — route injection" },
  "522":  { service: "ULP",              risk: "low",      note: "ULP" },
  "523":  { service: "IBM-DB2",          risk: "critical", note: "IBM DB2 — database exploitation" },
  "524":  { service: "NCP",              risk: "high",     note: "Novell NCP — file system access" },
  "525":  { service: "Timed",            risk: "low",      note: "timeserver" },
  "526":  { service: "Tempo",            risk: "low",      note: "newdate" },
  "527":  { service: "STX",              risk: "low",      note: "Stock IXChange" },
  "528":  { service: "Custix",           risk: "low",      note: "Customer IXChange" },
  "529":  { service: "IRC-SERV",         risk: "high",     note: "IRC-SERV — botnet infrastructure" },
  "530":  { service: "Courier (RPC)",    risk: "medium",   note: "courier" },
  "531":  { service: "Conference",       risk: "medium",   note: "chat" },
  "532":  { service: "Netnews",          risk: "low",      note: "readnews" },
  "533":  { service: "Netwall",          risk: "medium",   note: "netwall — emergency broadcasts" },
  "534":  { service: "MumPS",            risk: "low",      note: "MumPS" },
  "535":  { service: "IIOP",             risk: "medium",   note: "iiop" },
  "536":  { service: "Opalis Robot",     risk: "low",      note: "opalis-robot" },
  "537":  { service: "NMSP",             risk: "medium",   note: "Networked Media Streaming" },
  "538":  { service: "Gdomap",           risk: "medium",   note: "gdomap — GNUstep distributed objects" },
  "539":  { service: "Apertus Technologies",risk:"low",    note: "apertus-ldp" },
  "540":  { service: "UUCP",             risk: "high",     note: "uucpd — Unix to Unix Copy" },
  "541":  { service: "UUCP-RLOGIN",      risk: "critical", note: "uucp-rlogin — unencrypted remote login" },
  "542":  { service: "Commerce",         risk: "low",      note: "commerce" },
  "543":  { service: "Klogin",           risk: "high",     note: "Kerberos login — auth protocol" },
  "544":  { service: "Kshell",           risk: "high",     note: "Kerberos shell — unencrypted" },
  "545":  { service: "AppleTalk Mobile", risk: "low",      note: "appleqtcsrvr" },
  "546":  { service: "DHCPv6 Client",    risk: "medium",   note: "DHCPv6 — IPv6 address assignment" },
  "547":  { service: "DHCPv6 Server",    risk: "high",     note: "DHCPv6 — rogue DHCP attacks" },
  "548":  { service: "AFP over TCP",     risk: "high",     note: "Apple Filing Protocol — file system access" },
  "549":  { service: "IDFP",             risk: "low",      note: "IDFP" },
  "550":  { service: "New-Who",          risk: "medium",   note: "new-who — user enumeration" },
  "551":  { service: "CYBERCASH",        risk: "low",      note: "cybercash" },
  "552":  { service: "DeviceShare",      risk: "medium",   note: "deviceshare" },
  "553":  { service: "PIRP",             risk: "low",      note: "pirp" },
  "555":  { service: "DSF",              risk: "medium",   note: "dsf — also used by some RATs" },
  "556":  { service: "Remotefs",         risk: "high",     note: "rfs — Remote File System, no encryption" },
  "557":  { service: "Openvms-sysipc",   risk: "low",      note: "openvms-sysipc" },
  "558":  { service: "SDNSKMP",          risk: "low",      note: "SDNSKMP" },
  "559":  { service: "TEEDTAP",          risk: "low",      note: "TEEDTAP" },
  "560":  { service: "Rmonitor",         risk: "medium",   note: "rmonitord — remote monitoring" },
  "561":  { service: "Monitor",          risk: "medium",   note: "monitor" },
  "562":  { service: "Chshell",          risk: "high",     note: "chshell — command shell" },
  "563":  { service: "NNTP over TLS",    risk: "low",      note: "nntps" },
  "564":  { service: "9PFS",             risk: "medium",   note: "plan 9 file service" },
  "565":  { service: "Whoami",           risk: "medium",   note: "whoami — user identification" },
  "566":  { service: "Streettalk",       risk: "low",      note: "streettalk" },
  "567":  { service: "Banyan-RPC",       risk: "low",      note: "banyan-rpc" },
  "568":  { service: "MS Shuttle",       risk: "medium",   note: "microsoft shuttle" },
  "569":  { service: "MS Rome",          risk: "medium",   note: "microsoft rome" },
  "570":  { service: "Demon",            risk: "high",     note: "demon — remote access" },
  "571":  { service: "Udemon",           risk: "high",     note: "udemon — UDP remote access" },
  "572":  { service: "Sonar",            risk: "low",      note: "sonar" },
  "573":  { service: "Banyan-VIP",       risk: "low",      note: "banyan-vip" },
  "574":  { service: "FTP-Agent",        risk: "high",     note: "FTP Software Agent System" },
  "575":  { service: "VEMMI",            risk: "low",      note: "VEMMI" },
  "576":  { service: "IPCD",             risk: "low",      note: "ipcd" },
  "577":  { service: "VNAS",             risk: "low",      note: "vnas" },
  "578":  { service: "IPDD",             risk: "low",      note: "ipdd" },
  "579":  { service: "DECBSRV",          risk: "low",      note: "decbsrv" },
  "580":  { service: "SNTP-HEARTBEAT",   risk: "low",      note: "SNTP HEARTBEAT" },
  "581":  { service: "BDP",              risk: "low",      note: "Bundle Discovery Protocol" },
  "582":  { service: "SCC-Security",     risk: "medium",   note: "SCC Security" },
  "583":  { service: "Philips-vc",       risk: "low",      note: "Philips Video-Conferencing" },
  "584":  { service: "Key-Server",       risk: "high",     note: "Key Server — cryptographic key management" },
  "585":  { service: "IMAP4-SSL",        risk: "low",      note: "IMAP4+SSL" },
  "586":  { service: "Password-Chg",     risk: "high",     note: "Password Change — credential management" },
  "588":  { service: "CAL",              risk: "low",      note: "CAL" },
  "589":  { service: "EyelinkClient",    risk: "low",      note: "EyelinkClient" },
  "590":  { service: "TNS-CML",          risk: "low",      note: "TNS-CML" },
  "591":  { service: "HTTP Alt FileMaker",risk:"medium",   note: "FileMaker Inc. HTTP Alternate" },
  "592":  { service: "EUDORA",           risk: "low",      note: "Eudora Set" },
  "594":  { service: "TPIP",             risk: "low",      note: "TPIP" },
  "595":  { service: "CAB-Protocol",     risk: "low",      note: "CAB Protocol" },
  "596":  { service: "SMSD",             risk: "low",      note: "SMSD" },
  "597":  { service: "PTCNAMESERVICE",   risk: "low",      note: "PTC Name Service" },
  "598":  { service: "SCO Web Server",   risk: "medium",   note: "SCO Web Server Manager" },
  "599":  { service: "Aeolon Core",      risk: "low",      note: "Aeolon Core Protocol" },
  "600":  { service: "Sun IPC",          risk: "medium",   note: "Sun IPC server — Solaris" },
  "601":  { service: "Reliable Syslog",  risk: "medium",   note: "Reliable Syslog Service — log access" },
  "604":  { service: "TUNNEL",           risk: "high",     note: "TUNNEL profile — data tunneling" },
  "605":  { service: "SOAP-BEEP",        risk: "medium",   note: "SOAP BEEP" },
  "606":  { service: "URM",              risk: "low",      note: "Cray Unified Resource Manager" },
  "607":  { service: "NQS",              risk: "low",      note: "nqs" },
  "608":  { service: "Sender Initiated",  risk: "low",     note: "Sender-Initiated/Unsolicited File Transfer" },
  "609":  { service: "NPMP-TRAP",        risk: "low",      note: "npmp-trap" },
  "610":  { service: "NPMP-LOCAL",       risk: "low",      note: "npmp-local" },
  "611":  { service: "NPMP-GUI",         risk: "medium",   note: "npmp-gui — network printer management" },
  "612":  { service: "HMMP-IND",         risk: "low",      note: "HMMP Indication" },
  "613":  { service: "HMMP-OP",          risk: "low",      note: "HMMP Operation" },
  "614":  { service: "SSHELL",           risk: "high",     note: "SSLshell" },
  "615":  { service: "STS-SRM",          risk: "low",      note: "Internet Configuration Manager" },
  "616":  { service: "SCO-SYSMGR",       risk: "high",     note: "SCO System Administration Server" },
  "617":  { service: "SCO-DTMGR",        risk: "high",     note: "SCO Desktop Administration Server" },
  "618":  { service: "DEI-ICDA",         risk: "low",      note: "DEI-ICDA" },
  "619":  { service: "Compaq EVM",       risk: "medium",   note: "Compaq EVM" },
  "620":  { service: "SCO-WEBSRVRMG",    risk: "high",     note: "SCO WebServer Manager" },
  "621":  { service: "ESCP",             risk: "low",      note: "ESCP" },
  "622":  { service: "Collaborator",     risk: "low",      note: "Collaborator" },
  "624":  { service: "Crypto Admin",     risk: "high",     note: "Crypto Admin" },
  "625":  { service: "DEC DLM",          risk: "medium",   note: "DEC DLM" },
  "626":  { service: "ASIA",             risk: "low",      note: "ASIA" },
  "627":  { service: "PASSGO-TIVOLI",    risk: "high",     note: "PassGo Tivoli — authentication" },
  "628":  { service: "QMQP",             risk: "medium",   note: "QMQP" },
  "629":  { service: "3COM-AMP3",        risk: "medium",   note: "3Com AMP3" },
  "630":  { service: "RDA",              risk: "medium",   note: "RDA" },
  "632":  { service: "BMPP",             risk: "low",      note: "bmpp" },
  "633":  { service: "Service Status",   risk: "low",      note: "Service Status update" },
  "634":  { service: "GINAD",            risk: "low",      note: "ginad" },
  "635":  { service: "RLZDBASE",         risk: "low",      note: "RLZ DBase" },
  "637":  { service: "LAN-SERVER",       risk: "high",     note: "LAN Server" },
  "638":  { service: "MCNs-SEC",         risk: "medium",   note: "MCNs Security" },
  "639":  { service: "MSDP",             risk: "medium",   note: "MSDP — Multicast Source Discovery" },
  "640":  { service: "ENTRUST-SPS",      risk: "high",     note: "entrust-sps" },
  "641":  { service: "REPCMD",           risk: "low",      note: "repcmd" },
  "642":  { service: "ESRO-EMSDP",       risk: "low",      note: "ESRO-EMSDP" },
  "643":  { service: "SANITY",           risk: "low",      note: "SANity" },
  "644":  { service: "DWR",              risk: "low",      note: "dwr" },
  "645":  { service: "PSSC",             risk: "low",      note: "PSSC" },
  "646":  { service: "LDP",              risk: "high",     note: "LDP — MPLS Label Distribution" },
  "647":  { service: "DHCP-Failover",    risk: "high",     note: "DHCP Failover Protocol" },
  "648":  { service: "RRP",              risk: "medium",   note: "Registry Registrar Protocol" },
  "649":  { service: "CableTalk",        risk: "low",      note: "CableTalk" },
  "650":  { service: "OBEX",             risk: "medium",   note: "OBEX — file exchange, Bluetooth" },
  "651":  { service: "IEEE MMS",         risk: "low",      note: "IEEE MMS" },
  "652":  { service: "HELLO-PORT",       risk: "low",      note: "HELLO_PORT" },
  "653":  { service: "REPSCMD",          risk: "low",      note: "repscmd" },
  "654":  { service: "AODV",             risk: "medium",   note: "AODV — Ad hoc On-Demand Distance Vector" },
  "655":  { service: "TINC",             risk: "medium",   note: "TINC VPN" },
  "656":  { service: "SPMP",             risk: "low",      note: "spmp" },
  "657":  { service: "RMC",              risk: "medium",   note: "RMC — IBM Remote Monitoring" },
  "658":  { service: "TenFold",          risk: "low",      note: "TenFold" },
  "660":  { service: "MacOS Server Admin",risk:"high",     note: "MacOS Server Admin — admin access" },
  "661":  { service: "HAP",              risk: "low",      note: "HAP" },
  "662":  { service: "PFTP",             risk: "medium",   note: "PFTP" },
  "663":  { service: "PURENOISE",        risk: "low",      note: "purenoise" },
  "664":  { service: "Secure Aux Bus",   risk: "medium",   note: "Secure Aux Bus" },
  "665":  { service: "Sun DR",           risk: "medium",   note: "Sun DR" },
  "667":  { service: "DISCLOSE",         risk: "medium",   note: "Campaign contribution disclosures" },
  "668":  { service: "MECOMM",           risk: "low",      note: "MeComm" },
  "669":  { service: "MEREGISTER",       risk: "low",      note: "MeRegister" },
  "670":  { service: "VACDSM-SWS",       risk: "low",      note: "VACDSM-SWS" },
  "671":  { service: "VACDSM-APP",       risk: "low",      note: "VACDSM-APP" },
  "672":  { service: "VPPS-QUA",         risk: "low",      note: "VPPS-QUA" },
  "673":  { service: "CIMPLEX",          risk: "low",      note: "CIMPLEX" },
  "674":  { service: "ACAP",             risk: "medium",   note: "ACAP — Application Configuration" },
  "675":  { service: "DCTP",             risk: "low",      note: "DCTP" },
  "676":  { service: "VPPS-VIA",         risk: "low",      note: "VPPS Via" },
  "677":  { service: "VPP",              risk: "low",      note: "Virtual Presence Protocol" },
  "678":  { service: "GGF-NCP",          risk: "low",      note: "GNU Generation Foundation NCP" },
  "679":  { service: "MRM",              risk: "low",      note: "MRM" },
  "680":  { service: "ENTRUST-AAAS",     risk: "high",     note: "entrust-aaas — authentication" },
  "681":  { service: "ENTRUST-AAMS",     risk: "high",     note: "entrust-aams — management" },
  "682":  { service: "XFR",              risk: "medium",   note: "XFR" },
  "683":  { service: "CORBA-IIOP",       risk: "medium",   note: "CORBA IIOP" },
  "684":  { service: "CORBA-IIOP-SSL",   risk: "medium",   note: "CORBA IIOP SSL" },
  "685":  { service: "MDL",              risk: "low",      note: "MDL" },
  "686":  { service: "Hardware Control", risk: "high",     note: "Hardware Control Protocol — physical device access" },
  "687":  { service: "ASIPREGISTRY",     risk: "low",      note: "asipregistry" },
  "688":  { service: "REALM-RUSD",       risk: "high",     note: "REALM-RUSD — authentication realm" },
  "689":  { service: "NMAP",             risk: "critical", note: "Official NMAP port — active scanner" },
  "690":  { service: "VATP",             risk: "low",      note: "Velazquez Application Protocol" },
  "692":  { service: "Hyperwave-ISP",    risk: "low",      note: "Hyperwave-ISP" },
  "693":  { service: "CONNENDP",         risk: "low",      note: "connendp" },
  "694":  { service: "HA-Cluster",       risk: "high",     note: "Linux-HA high availability — cluster mgmt" },
  "695":  { service: "IEEE-MMS-SSL",     risk: "low",      note: "IEEE MMS SSL" },
  "696":  { service: "RUSHD",            risk: "medium",   note: "RUSHD" },
  "697":  { service: "UUIDGEN",          risk: "low",      note: "UUIDGEN" },
  "698":  { service: "OLSR",             risk: "medium",   note: "OLSR — mesh routing protocol" },
  "699":  { service: "Access Network",   risk: "medium",   note: "Access Network" },
  "700":  { service: "EPP",              risk: "medium",   note: "Extensible Provisioning Protocol" },
  "701":  { service: "LMP",              risk: "medium",   note: "Link Management Protocol" },
  "702":  { service: "IRIS-BEEP",        risk: "low",      note: "IRIS over BEEP" },
  "704":  { service: "Erlog Copy",       risk: "low",      note: "errlog copy/server daemon" },
  "706":  { service: "SILC",             risk: "medium",   note: "Secure Internet Live Conferencing" },
  "707":  { service: "Borland DSJ",      risk: "medium",   note: "Borland DSJ" },
  "709":  { service: "Entrust Key Mgmt", risk: "high",     note: "Entrust Key Management Service Handler" },
  "710":  { service: "Entrust Admin",    risk: "high",     note: "Entrust Administration Service Handler" },
  "711":  { service: "TDP",              risk: "low",      note: "Cisco TDP — Tag Distribution" },
  "712":  { service: "TBRPF",            risk: "medium",   note: "Topology Broadcast Reverse-Path" },
  "713":  { service: "Iris-XPC",         risk: "low",      note: "IRIS over XPC" },
  "714":  { service: "Iris-XPCS",        risk: "low",      note: "IRIS over XPCS" },
  "715":  { service: "Iris-LWZ",         risk: "low",      note: "IRIS-LWZ" },
  "716":  { service: "PANA",             risk: "medium",   note: "PANA — Protocol for Auth of Network Access" },
  "729":  { service: "IBM NetView DM",   risk: "medium",   note: "IBM NetView DM/6000" },
  "730":  { service: "IBM NetView DM",   risk: "medium",   note: "IBM NetView DM/6000 Server" },
  "731":  { service: "IBM NetView DM",   risk: "medium",   note: "IBM NetView DM/6000 Client" },
  "741":  { service: "NetGW",            risk: "medium",   note: "netGW" },
  "742":  { service: "NETRCS",           risk: "medium",   note: "Network based Rev. Cont. Sys." },
  "744":  { service: "Flexible License", risk: "medium",   note: "Flexible License Manager" },
  "747":  { service: "Fujitsu Db Bkup",  risk: "high",     note: "Fujitsu Device Management — backup" },
  "748":  { service: "Fujitsu DeviceCtrl",risk:"high",     note: "Fujitsu Device Control" },
  "749":  { service: "Kerberos Admin",   risk: "critical", note: "Kerberos 5 Administration — domain compromise" },
  "750":  { service: "Kerberos IV",      risk: "high",     note: "Kerberos IV — deprecated, weaknesses known" },
  "751":  { service: "Kerberos Master",  risk: "critical", note: "Kerberos master server" },
  "752":  { service: "Passwd",           risk: "critical", note: "Kerberos passwd server" },
  "753":  { service: "Reverse Routing",  risk: "medium",   note: "RRH — Reverse Routing Header" },
  "754":  { service: "Send",             risk: "medium",   note: "send" },
  "758":  { service: "NLOGIN",           risk: "high",     note: "nlogin — network login" },
  "759":  { service: "CON",              risk: "low",      note: "con" },
  "760":  { service: "NS",               risk: "low",      note: "ns" },
  "761":  { service: "RXE",              risk: "medium",   note: "rxe" },
  "762":  { service: "QUOTAD",           risk: "low",      note: "quotad" },
  "763":  { service: "CYCLESERV",        risk: "low",      note: "cycleserv" },
  "764":  { service: "OMSERV",           risk: "low",      note: "omserv" },
  "765":  { service: "WEBSTER",          risk: "low",      note: "webster" },
  "767":  { service: "Phonebook",        risk: "low",      note: "phone" },
  "769":  { service: "VID",              risk: "low",      note: "vid" },
  "770":  { service: "CADLOCK",          risk: "low",      note: "cadlock" },
  "772":  { service: "CYCLESERV2",       risk: "low",      note: "cycleserv2" },
  "773":  { service: "Submit",           risk: "medium",   note: "notify" },
  "774":  { service: "RPasswd",          risk: "high",     note: "rpasswd — remote password change" },
  "775":  { service: "Entomb",           risk: "low",      note: "entomb" },
  "776":  { service: "Wpages",           risk: "low",      note: "wpages" },
  "780":  { service: "WPGS",             risk: "low",      note: "wpgs" },
  "781":  { service: "HP Collector",     risk: "medium",   note: "hp-collector" },
  "782":  { service: "HP ManagedNode",   risk: "medium",   note: "hp-managed-node" },
  "783":  { service: "SpamAssassin",     risk: "low",      note: "spamassassin daemon" },
  "786":  { service: "Concert",          risk: "low",      note: "concert" },
  "787":  { service: "QSC",              risk: "low",      note: "qsc" },
  "799":  { service: "CONTROLIT",        risk: "low",      note: "Controlit" },
  "800":  { service: "mdbs_daemon",      risk: "medium",   note: "mdbs_daemon" },
  "801":  { service: "Device",           risk: "low",      note: "device" },
  "810":  { service: "FCP-UDP",          risk: "medium",   note: "FCP" },
  "828":  { service: "ITM-MCELL-S",      risk: "low",      note: "itm-mcell-s" },
  "829":  { service: "PKIX-3-CA-RA",     risk: "medium",   note: "PKIX-3 CA/RA — certificate management" },
  "830":  { service: "NETCONF over SSH", risk: "high",     note: "NETCONF — network device config" },
  "831":  { service: "NETCONF over BEEP",risk: "high",     note: "NETCONF over BEEP" },
  "832":  { service: "NETCONF/SOAP+HTTPS",risk:"high",     note: "NETCONF/SOAP over HTTPS" },
  "833":  { service: "NETCONF/SOAP+HTTP", risk:"high",     note: "NETCONF/SOAP over HTTP" },
  "847":  { service: "DHCP-FAILOVER-2",  risk: "high",     note: "DHCP Failover 2" },
  "848":  { service: "GDOI",             risk: "high",     note: "GDOI — Group Domain of Interpretation" },
  "853":  { service: "DNS over TLS",     risk: "medium",   note: "DoT — DNS over TLS" },
  "860":  { service: "ISCSI",            risk: "critical", note: "iSCSI — storage area network access" },
  "861":  { service: "OWAMP-Control",    risk: "low",      note: "OWAMP-Control" },
  "862":  { service: "TWAMP-Control",    risk: "low",      note: "TWAMP-Control" },
  "873":  { service: "Rsync",            risk: "high",     note: "rsync — file transfer, often misconfigured" },
  "888":  { service: "CDDBP",            risk: "medium",   note: "CD Database Protocol" },
  "900":  { service: "OMG CORBA",        risk: "medium",   note: "OMG Initial Refs" },
  "901":  { service: "SAMBA-SWAT",       risk: "high",     note: "SAMBA Web Admin Tool" },
  "903":  { service: "VMware Auth",      risk: "high",     note: "VMware Authentication Daemon" },
  "911":  { service: "Network Console",  risk: "critical", note: "Network Console on Acid" },
  "953":  { service: "RNDC",             risk: "high",     note: "BIND RNDC — DNS admin control" },
  "981":  { service: "SofaWare MGMT",    risk: "high",     note: "Remote HTTPS management for SofaWare" },
  "987":  { service: "SofaWare MGMT",    risk: "high",     note: "SofaWare Technologies MGMT" },
  "992":  { service: "Telnet over TLS",  risk: "medium",   note: "telnet protocol over TLS/SSL" },
  "994":  { service: "IRC over TLS",     risk: "medium",   note: "IRC over TLS" },
  "996":  { service: "VSINET",           risk: "low",      note: "vsinet" },
  "997":  { service: "MAITRD",           risk: "low",      note: "maitrd" },
  "998":  { service: "BUSBOY",           risk: "low",      note: "busboy" },
  "999":  { service: "GARCON",           risk: "medium",   note: "garcon — also used by some malware" },

  // ── Registered Ports (1024–49151) ────────────────────────────────────────────
  "1080": { service: "SOCKS Proxy",     risk: "high",     note: "Open proxy — anonymization / C2 tunneling" },
  "1194": { service: "OpenVPN",         risk: "medium",   note: "VPN — legitimate but tunneling risk" },
  "1214": { service: "Kazaa",           risk: "medium",   note: "P2P — malware distribution vector" },
  "1241": { service: "Nessus",          risk: "high",     note: "Vulnerability scanner — recon if unexpected" },
  "1433": { service: "MSSQL",           risk: "critical", note: "SQL Server — SQLi, brute force, lateral movement" },
  "1434": { service: "MSSQL Monitor",   risk: "critical", note: "SQL Slammer worm target — UDP amplification" },
  "1521": { service: "Oracle DB",       risk: "critical", note: "Oracle — TNS Poison, brute force, SQLi" },
  "1604": { service: "Citrix ICA",      risk: "high",     note: "Citrix — credential harvesting" },
  "1723": { service: "PPTP VPN",        risk: "high",     note: "MS-CHAPv2 — broken encryption" },
  "1883": { service: "MQTT",            risk: "high",     note: "IoT protocol — often unauthenticated" },
  "1900": { service: "UPnP/SSDP",       risk: "high",     note: "SSDP amplification DDoS — billions of devices" },
  "2049": { service: "NFS",             risk: "critical", note: "Network file system — often misconfigured" },
  "2082": { service: "cPanel HTTP",     risk: "high",     note: "Web hosting panel — brute force target" },
  "2083": { service: "cPanel HTTPS",    risk: "high",     note: "Web hosting panel — brute force target" },
  "2100": { service: "Oracle XDB FTP",  risk: "high",     note: "Oracle XML DB" },
  "2181": { service: "ZooKeeper",       risk: "critical", note: "Distributed coordination — no auth by default" },
  "2375": { service: "Docker HTTP",     risk: "critical", note: "Docker daemon unencrypted — RCE if exposed" },
  "2376": { service: "Docker HTTPS",    risk: "high",     note: "Docker daemon TLS — cert validation attacks" },
  "2379": { service: "etcd",            risk: "critical", note: "Kubernetes config store — credential dump" },
  "2404": { service: "IEC 60870-5-104", risk: "critical", note: "ICS/SCADA power grid protocol — no auth" },
  "2483": { service: "Oracle DB TLS",   risk: "high",     note: "Oracle encrypted listener" },
  "3000": { service: "Dev Server",      risk: "medium",   note: "Common dev port — should not be internet-facing" },
  "3268": { service: "Global Catalog",  risk: "high",     note: "Active Directory — LDAP enumeration" },
  "3269": { service: "Global Catalog TLS",risk:"high",    note: "Active Directory encrypted" },
  "3299": { service: "SAP",             risk: "critical", note: "SAP Router — ERP system attack surface" },
  "3306": { service: "MySQL",           risk: "critical", note: "Database — SQLi, brute force, data exfiltration" },
  "3389": { service: "RDP",             risk: "critical", note: "BlueKeep, DejaBlue — ransomware primary vector" },
  "3690": { service: "SVN",             risk: "high",     note: "Source control — code theft" },
  "4369": { service: "RabbitMQ/Erlang", risk: "high",     note: "Message broker — Erlang cookie attack" },
  "4444": { service: "Metasploit",      risk: "critical", note: "Default Metasploit/Meterpreter listener" },
  "4500": { service: "IPSec NAT-T",     risk: "high",     note: "VPN NAT traversal" },
  "4786": { service: "Cisco Smart Install",risk:"critical",note:"Cisco switch takeover — no auth required" },
  "4848": { service: "GlassFish Admin", risk: "critical", note: "Java EE admin — default creds" },
  "5000": { service: "Docker Registry / Flask",risk:"high",note:"Container registry or dev server — often exposed" },
  "5001": { service: "Synology/Flask",  risk: "high",     note: "NAS admin or dev server" },
  "5004": { service: "RTP",             risk: "medium",   note: "VoIP media — eavesdropping" },
  "5060": { service: "SIP",             risk: "high",     note: "VoIP — toll fraud, eavesdropping" },
  "5432": { service: "PostgreSQL",      risk: "critical", note: "Database — brute force, SQLi, data exfiltration" },
  "5555": { service: "ADB/HP Data Protector",risk:"critical",note:"Android debug bridge / HP backup — RCE" },
  "5601": { service: "Kibana",          risk: "critical", note: "ELK dashboard — often exposed without auth" },
  "5800": { service: "VNC HTTP",        risk: "high",     note: "VNC web interface — remote desktop" },
  "5900": { service: "VNC",             risk: "critical", note: "Remote desktop — weak passwords common" },
  "5985": { service: "WinRM HTTP",      risk: "critical", note: "Windows remote management — lateral movement" },
  "5986": { service: "WinRM HTTPS",     risk: "high",     note: "Windows remote management encrypted" },
  "6379": { service: "Redis",           risk: "critical", note: "No auth by default — RCE via config write" },
  "6443": { service: "Kubernetes API",  risk: "critical", note: "K8s cluster API — container escape" },
  "6666": { service: "IRC Alt",         risk: "critical", note: "Known malware C2 port" },
  "6667": { service: "IRC",             risk: "high",     note: "Botnet C2 — IRC-based malware" },
  "6881": { service: "BitTorrent",      risk: "medium",   note: "P2P — malware distribution" },
  "7001": { service: "WebLogic",        risk: "critical", note: "Oracle WebLogic — multiple critical RCEs" },
  "7474": { service: "Neo4j",           risk: "critical", note: "Graph DB — often exposed without auth" },
  "8000": { service: "Dev HTTP",        risk: "medium",   note: "Common dev server port" },
  "8080": { service: "HTTP Alt/Proxy",  risk: "medium",   note: "Web proxy, Tomcat — common attack surface" },
  "8081": { service: "HTTP Alt",        risk: "medium",   note: "Alternate HTTP" },
  "8088": { service: "Hadoop YARN",     risk: "critical", note: "Hadoop resource manager — RCE without auth" },
  "8161": { service: "ActiveMQ Admin",  risk: "critical", note: "Apache ActiveMQ — Log4Shell / RCE history" },
  "8443": { service: "HTTPS Alt",       risk: "medium",   note: "Alternate HTTPS — Tomcat admin" },
  "8888": { service: "Jupyter/HTTP Alt",risk:"critical",  note: "Jupyter Notebook — code execution if exposed" },
  "9000": { service: "PHP-FPM/SonarQube",risk:"high",     note: "PHP backend or code analysis — exposed admin" },
  "9042": { service: "Cassandra",       risk: "critical", note: "NoSQL DB — no auth by default" },
  "9090": { service: "Prometheus",      risk: "high",     note: "Monitoring — metrics leak, no auth common" },
  "9092": { service: "Kafka",           risk: "high",     note: "Message queue — data interception" },
  "9200": { service: "Elasticsearch",   risk: "critical", note: "No auth by default — data exfiltration at scale" },
  "9300": { service: "Elasticsearch Cluster",risk:"critical",note:"Cluster transport — data exfiltration" },
  "10250":{ service: "Kubelet",         risk: "critical", note: "Kubernetes node agent — RCE on container" },
  "11211":{ service: "Memcached",       risk: "critical", note: "UDP amplification DDoS — 51,000x amplification" },
  "27017":{ service: "MongoDB",         risk: "critical", note: "No auth by default — mass data exposure" },
  "27018":{ service: "MongoDB Shard",   risk: "critical", note: "MongoDB sharding — auth bypass" },
  "28017":{ service: "MongoDB HTTP",    risk: "critical", note: "MongoDB web UI — often exposed" },
  "50000":{ service: "SAP Dispatcher",  risk: "critical", note: "SAP ICM — remote code execution" },
  "50070":{ service: "Hadoop NameNode", risk: "critical", note: "HDFS admin — data access without auth" },

  // ── Dynamic / Ephemeral (49152–65535) ─────────────────────────────────────────
  "49152":{ service: "Ephemeral Start", risk: "low",      note: "Dynamic port range start — typically client-side" },
  "65535":{ service: "Ephemeral End",   risk: "low",      note: "Dynamic port range end" },
};

// Port range fallback for unknown ports
export function getPortRiskByRange(port) {
  const p = parseInt(port);
  if (isNaN(p)) return { service: "Invalid", risk: "unknown", note: "Invalid port number" };
  if (p < 0 || p > 65535) return { service: "Out of Range", risk: "unknown", note: "Port out of valid range 0–65535" };
  if (p === 0) return { service: "Reserved", risk: "high", note: "Port 0 — should never appear in legitimate traffic" };
  if (p <= 1023) return { service: `Well-Known Port ${p}`, risk: "medium", note: `IANA well-known port — check service assignment at iana.org/assignments/service-names-port-numbers` };
  if (p <= 49151) return { service: `Registered Port ${p}`, risk: "low", note: `Registered port — may be vendor-specific or custom application` };
  return { service: `Ephemeral Port ${p}`, risk: "low", note: `Dynamic/ephemeral port — typically assigned by OS for client connections` };
}

// Attack pattern intel
const ATTACK_INTEL = {
  "portscan":     { severity: "medium", mitre: "T1046",  desc: "Network Service Discovery",        remediation: "Enable IDS signatures, restrict outbound ICMP" },
  "dos":          { severity: "high",   mitre: "T1498",  desc: "Network Denial of Service",        remediation: "Enable rate limiting, DDoS protection, upstream scrubbing" },
  "ddos":         { severity: "critical",mitre:"T1498",  desc: "Distributed Denial of Service",    remediation: "CDN/DDoS mitigation service required" },
  "brute":        { severity: "high",   mitre: "T1110",  desc: "Brute Force Authentication",       remediation: "Enable MFA, account lockout, IP allowlisting" },
  "bruteforce":   { severity: "high",   mitre: "T1110",  desc: "Brute Force Authentication",       remediation: "Enable MFA, account lockout, IP allowlisting" },
  "sqlinject":    { severity: "critical",mitre:"T1190",  desc: "SQL Injection — Exploit Public App",remediation: "Parameterized queries, WAF, input validation" },
  "injection":    { severity: "critical",mitre:"T1190",  desc: "Injection Attack",                 remediation: "Input validation, WAF deployment" },
  "xss":          { severity: "high",   mitre: "T1059",  desc: "Cross-Site Scripting",             remediation: "CSP headers, output encoding, WAF" },
  "mitm":         { severity: "critical",mitre:"T1557",  desc: "Man-in-the-Middle",                remediation: "TLS enforcement, certificate pinning, HSTS" },
  "malware":      { severity: "critical",mitre:"T1204",  desc: "Malware Execution",                remediation: "EDR deployment, application allowlisting, user training" },
  "ransomware":   { severity: "critical",mitre:"T1486",  desc: "Data Encrypted for Impact",        remediation: "Offline backups, EDR, network segmentation" },
  "exfiltrat":    { severity: "critical",mitre:"T1048",  desc: "Exfiltration Over Alternative Protocol",remediation: "DLP, egress filtering, UEBA" },
  "phishing":     { severity: "high",   mitre: "T1566",  desc: "Phishing",                         remediation: "Email filtering, user training, MFA" },
  "reconnaissance":{ severity: "medium",mitre:"T1595",  desc: "Active Scanning",                  remediation: "Honeypots, firewall rules, network monitoring" },
  "lateral":      { severity: "critical",mitre:"T1021",  desc: "Lateral Movement",                 remediation: "Network segmentation, PAM, EDR" },
  "privilege":    { severity: "critical",mitre:"T1068",  desc: "Privilege Escalation",             remediation: "Patch management, least privilege, PAM" },
  "normal":       { severity: "clean",  mitre: "N/A",    desc: "Normal/Benign Traffic",            remediation: "No action required" },
  "benign":       { severity: "clean",  mitre: "N/A",    desc: "Benign Traffic",                   remediation: "No action required" },
};

function lookupAttack(name) {
  const n = name.toLowerCase().replace(/[\s_-]/g, "");
  for (const [key, val] of Object.entries(ATTACK_INTEL)) {
    if (n.includes(key)) return { ...val, matched: key };
  }
  return { severity: "unknown", mitre: "Unknown", desc: name, remediation: "Investigate further", matched: null };
}

function lookupPort(port) {
  const exact = PORT_INTEL[String(port)];
  if (exact) return { ...exact, port, matched: "exact" };
  // Fall back to range-based classification
  const range = getPortRiskByRange(port);
  return { ...range, port, matched: "range" };
}

export default function ThreatIntel() {
  const [ipInput, setIpInput]       = useState("");
  const [portInput, setPortInput]   = useState("");
  const [attackInput, setAttackInput] = useState("");
  const [ipResults, setIpResults]   = useState([]);
  const [portResult, setPortResult] = useState(null);
  const [attackResult, setAttackResult] = useState(null);
  const [ipLoading, setIpLoading]   = useState(false);
  const [activeTab, setActiveTab]   = useState("ip");

  // ── IP Lookup (uses ip-api.com free tier) ────────────────────────────────────

  const lookupIPs = useCallback(async () => {
    const ips = [...new Set(ipInput.split(/[\n,\s]+/).map(s => s.trim()).filter(Boolean))].slice(0, 20);
    if (!ips.length) return;
    setIpLoading(true);
    setIpResults([]);

    const results = [];
    for (const ip of ips) {
      // Check local threat DB first
      let localThreat = null;
      for (const [prefix, intel] of Object.entries(THREAT_DB)) {
        if (ip.startsWith(prefix)) { localThreat = intel; break; }
      }

      try {
        const res = await fetch(`https://ip-api.com/json/${ip}?fields=status,country,regionName,city,isp,org,as,proxy,hosting,query`);
        const geo = await res.json();
        results.push({
          ip,
          country:  geo.country || "Unknown",
          region:   geo.regionName || "",
          city:     geo.city || "",
          isp:      geo.isp || "Unknown",
          org:      geo.org || "",
          asn:      geo.as || "",
          proxy:    geo.proxy,
          hosting:  geo.hosting,
          risk:     localThreat?.risk || (geo.proxy ? "high" : geo.hosting ? "medium" : "unknown"),
          type:     localThreat?.type || (geo.proxy ? "Proxy/VPN" : geo.hosting ? "Hosting/DC" : "Residential/Business"),
          tags:     localThreat?.tags || [...(geo.proxy ? ["proxy"] : []), ...(geo.hosting ? ["hosting","datacenter"] : [])],
          status:   geo.status
        });
      } catch {
        results.push({ ip, country: "Lookup failed", risk: "unknown", type: "Error", tags: [], status: "fail" });
      }

      // ip-api free tier: max 45 req/min — small delay
      await new Promise(r => setTimeout(r, 80));
    }

    setIpResults(results);
    setIpLoading(false);
  }, [ipInput]);

  const lookupPortFn = () => {
    const p = portInput.trim();
    if (!p) return;
    setPortResult(lookupPort(p));
  };

  const lookupAttackFn = () => {
    const a = attackInput.trim();
    if (!a) return;
    setAttackResult(lookupAttack(a));
  };

  const tabs = [
    { id: "ip",     label: "IP Lookup" },
    { id: "port",   label: "Port Intel" },
    { id: "attack", label: "Attack Intel" },
    { id: "ref",    label: "Reference" },
  ];

  const MITRE_TACTICS = [
    { id: "TA0001", name: "Initial Access",     techniques: ["T1190 – Exploit Public App", "T1566 – Phishing", "T1195 – Supply Chain"] },
    { id: "TA0002", name: "Execution",          techniques: ["T1059 – Command & Scripting", "T1204 – User Execution", "T1106 – Native API"] },
    { id: "TA0003", name: "Persistence",        techniques: ["T1098 – Account Manipulation", "T1053 – Scheduled Task", "T1543 – System Service"] },
    { id: "TA0004", name: "Privilege Escalation",techniques:["T1068 – Exploit for Priv Esc", "T1548 – Abuse Elevation Control", "T1134 – Access Token"] },
    { id: "TA0005", name: "Defense Evasion",    techniques: ["T1036 – Masquerading", "T1070 – Indicator Removal", "T1027 – Obfuscated Files"] },
    { id: "TA0006", name: "Credential Access",  techniques: ["T1110 – Brute Force", "T1555 – Credentials from Stores", "T1003 – OS Credential Dump"] },
    { id: "TA0007", name: "Discovery",          techniques: ["T1046 – Network Service Scan", "T1082 – System Info", "T1595 – Active Scanning"] },
    { id: "TA0008", name: "Lateral Movement",   techniques: ["T1021 – Remote Services", "T1091 – Removable Media", "T1557 – MitM"] },
    { id: "TA0009", name: "Collection",         techniques: ["T1005 – Local Data", "T1039 – Network Share", "T1113 – Screen Capture"] },
    { id: "TA0010", name: "Exfiltration",       techniques: ["T1048 – Alt Protocol Exfil", "T1041 – Exfil Over C2", "T1567 – Web Service"] },
    { id: "TA0011", name: "Command & Control",  techniques: ["T1071 – App Layer Protocol", "T1095 – Non-App Layer", "T1572 – Protocol Tunneling"] },
    { id: "TA0040", name: "Impact",             techniques: ["T1486 – Data Encrypted", "T1498 – Network DoS", "T1491 – Defacement"] },
  ];

  return (
    <PageLayout title="Threat Intelligence" subtitle="IP LOOKUP · PORT INTEL · ATTACK PATTERNS · MITRE ATT&CK">

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${theme.colors.cardBorder}`, paddingBottom: 1 }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: "10px 20px", background: "transparent", border: "none",
            borderBottom: activeTab === tab.id ? `2px solid #14B8A6` : "2px solid transparent",
            color: activeTab === tab.id ? "#14B8A6" : theme.colors.textMuted,
            fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, letterSpacing: 1,
            cursor: "pointer", transition: "all 0.2s"
          }}>{tab.label.toUpperCase()}</button>
        ))}
      </div>

      {/* ═══ IP LOOKUP ═══ */}
      {activeTab === "ip" && (
        <>
          <Card>
            <ChartTitle color="#14B8A6">IP Address Lookup</ChartTitle>
            <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted, marginBottom: 12 }}>
              Enter one or more IP addresses (comma, space, or newline separated) · Max 20 IPs · Uses ip-api.com free geolocation
            </div>
            <textarea
              value={ipInput}
              onChange={e => setIpInput(e.target.value)}
              placeholder={"192.168.1.1\n10.0.0.5\n8.8.8.8"}
              rows={5}
              style={{
                width: "100%", background: theme.colors.inputBg,
                border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 8,
                color: theme.colors.textPrimary, fontFamily: theme.fonts.mono,
                fontSize: 13, padding: "12px", outline: "none", resize: "vertical"
              }}
            />
            <button onClick={lookupIPs} disabled={ipLoading || !ipInput.trim()} style={{
              marginTop: 12, padding: "10px 24px",
              background: ipLoading ? "rgba(20,184,166,0.2)" : "linear-gradient(135deg, #14B8A6, #0D9488)",
              border: "none", borderRadius: 8, color: theme.colors.buttonText,
              fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 12, letterSpacing: 1,
              cursor: ipLoading ? "not-allowed" : "pointer"
            }}>{ipLoading ? "LOOKING UP..." : "LOOKUP IPs"}</button>
          </Card>

          {ipResults.length > 0 && (
            <Card style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: theme.fonts.mono, fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: theme.colors.inputBg }}>
                      {["IP Address", "Risk", "Type", "Country", "City", "ISP / Org", "ASN", "Flags"].map(h => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: theme.colors.textLabel,
                          borderBottom: `1px solid ${theme.colors.cardBorder}`, whiteSpace: "nowrap", letterSpacing: 1, fontSize: 11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ipResults.map((r, i) => {
                      const rc = RISK_COLOR[r.risk] || RISK_COLOR.unknown;
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${theme.colors.cardBorder}22`, background: i % 2 === 0 ? theme.colors.rowAlt : "transparent" }}>
                          <td style={{ padding: "9px 14px", color: "#14B8A6", fontWeight: 700 }}>{r.ip}</td>
                          <td style={{ padding: "9px 14px" }}><RiskBadge level={r.risk} /></td>
                          <td style={{ padding: "9px 14px", color: theme.colors.textSecondary }}>{r.type}</td>
                          <td style={{ padding: "9px 14px", color: theme.colors.textPrimary }}>{r.country}</td>
                          <td style={{ padding: "9px 14px", color: theme.colors.textSecondary }}>{r.city}{r.region ? `, ${r.region}` : ""}</td>
                          <td style={{ padding: "9px 14px", color: theme.colors.textSecondary, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{r.isp || r.org}</td>
                          <td style={{ padding: "9px 14px", color: theme.colors.textMuted }}>{r.asn}</td>
                          <td style={{ padding: "9px 14px" }}>
                            {r.proxy && <span style={{ padding: "1px 6px", borderRadius: 3, background: "#FF3D5722", color: theme.colors.danger, fontSize: 9, marginRight: 4 }}>PROXY</span>}
                            {r.hosting && <span style={{ padding: "1px 6px", borderRadius: 3, background: "#FFB80022", color: theme.colors.warning, fontSize: 9 }}>HOSTING</span>}
                            {r.tags?.map(t => <span key={t} style={{ padding: "1px 6px", borderRadius: 3, background: "rgba(0,212,255,0.1)", color: theme.colors.accent, fontSize: 9, marginLeft: 2 }}>{t}</span>)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {/* ═══ PORT INTEL ═══ */}
      {activeTab === "port" && (
        <>
          <Card>
            <ChartTitle color="#14B8A6">Port Intelligence Lookup</ChartTitle>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 6 }}>PORT NUMBER</div>
                <input value={portInput} onChange={e => setPortInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && lookupPortFn()}
                  placeholder="e.g. 445, 3389, 22..."
                  style={{
                    width: "100%", background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`,
                    borderRadius: 8, color: theme.colors.textPrimary, fontFamily: theme.fonts.mono,
                    fontSize: 14, padding: "12px", outline: "none"
                  }} />
              </div>
              <button onClick={lookupPortFn} style={{
                padding: "12px 24px", background: "linear-gradient(135deg, #14B8A6, #0D9488)",
                border: "none", borderRadius: 8, color: theme.colors.buttonText,
                fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 12, letterSpacing: 1, cursor: "pointer"
              }}>LOOKUP</button>
            </div>
          </Card>

          {portResult && (
            <Card>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div>
                  <div style={{ fontFamily: theme.fonts.display, fontSize: 28, fontWeight: 900, color: "#14B8A6" }}>Port {portInput}</div>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 16, color: theme.colors.textPrimary, marginTop: 4 }}>{portResult.service}</div>
                  <div style={{ marginTop: 12 }}><RiskBadge level={portResult.risk} /></div>
                  <div style={{ fontFamily: theme.fonts.body, fontSize: 14, color: theme.colors.textSecondary, marginTop: 16, lineHeight: 1.6 }}>{portResult.note}</div>
                </div>
                <div style={{ background: theme.colors.inputBg, borderRadius: 10, padding: 20 }}>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 8 }}>THREAT ASSESSMENT</div>
                  {[
                    { label: "Service", value: portResult.service },
                    { label: "Risk Level", value: portResult.risk.toUpperCase() },
                    { label: "Threat Note", value: portResult.note },
                    { label: "Recommendation", value: portResult.risk === "critical" || portResult.risk === "high"
                      ? "Block or restrict access. Monitor all traffic on this port."
                      : portResult.risk === "medium"
                      ? "Monitor traffic. Ensure service is properly secured."
                      : "Standard monitoring recommended." }
                  ].map(({ label, value }) => (
                    <div key={label} style={{ marginBottom: 12 }}>
                      <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, letterSpacing: 1 }}>{label}</div>
                      <div style={{ fontFamily: theme.fonts.body, fontSize: 13, color: theme.colors.textSecondary, marginTop: 2 }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Port reference table */}
          <Card>
            <ChartTitle color="#14B8A6">Common High-Risk Ports Reference</ChartTitle>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: theme.fonts.mono, fontSize: 12 }}>
                <thead>
                  <tr style={{ background: theme.colors.inputBg }}>
                    {["Port", "Service", "Risk", "Threat Note"].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: theme.colors.textLabel,
                        borderBottom: `1px solid ${theme.colors.cardBorder}`, fontSize: 11, letterSpacing: 1 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(PORT_INTEL).sort((a, b) => {
                    const order = { critical: 0, high: 1, medium: 2, low: 3, unknown: 4 };
                    return (order[a[1].risk] || 4) - (order[b[1].risk] || 4);
                  }).map(([port, info], i) => (
                    <tr key={port} style={{ borderBottom: `1px solid ${theme.colors.cardBorder}22`, background: i % 2 === 0 ? theme.colors.rowAlt : "transparent",
                      cursor: "pointer" }} onClick={() => { setPortInput(port); setPortResult(info); }}>
                      <td style={{ padding: "8px 14px", color: "#14B8A6", fontWeight: 700 }}>{port}</td>
                      <td style={{ padding: "8px 14px", color: theme.colors.textPrimary }}>{info.service}</td>
                      <td style={{ padding: "8px 14px" }}><RiskBadge level={info.risk} /></td>
                      <td style={{ padding: "8px 14px", color: theme.colors.textSecondary }}>{info.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* ═══ ATTACK INTEL ═══ */}
      {activeTab === "attack" && (
        <>
          <Card>
            <ChartTitle color="#14B8A6">Attack Type Intelligence</ChartTitle>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 6 }}>ATTACK NAME / TYPE</div>
                <input value={attackInput} onChange={e => setAttackInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && lookupAttackFn()}
                  placeholder="e.g. DDoS, SQL Injection, Brute Force, Ransomware..."
                  style={{
                    width: "100%", background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`,
                    borderRadius: 8, color: theme.colors.textPrimary, fontFamily: theme.fonts.mono,
                    fontSize: 14, padding: "12px", outline: "none"
                  }} />
              </div>
              <button onClick={lookupAttackFn} style={{
                padding: "12px 24px", background: "linear-gradient(135deg, #14B8A6, #0D9488)",
                border: "none", borderRadius: 8, color: theme.colors.buttonText,
                fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 12, letterSpacing: 1, cursor: "pointer"
              }}>LOOKUP</button>
            </div>
          </Card>

          {attackResult && (
            <Card>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div>
                  <div style={{ fontFamily: theme.fonts.display, fontSize: 22, fontWeight: 900, color: "#14B8A6", letterSpacing: 1 }}>{attackInput}</div>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 13, color: theme.colors.textSecondary, marginTop: 4 }}>{attackResult.desc}</div>
                  <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
                    <RiskBadge level={attackResult.severity} />
                    {attackResult.mitre !== "N/A" && attackResult.mitre !== "Unknown" && (
                      <span style={{ padding: "3px 10px", borderRadius: 4, background: "rgba(20,184,166,0.15)", border: "1px solid rgba(20,184,166,0.3)", color: "#14B8A6", fontFamily: theme.fonts.mono, fontSize: 10 }}>
                        MITRE {attackResult.mitre}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ background: theme.colors.inputBg, borderRadius: 10, padding: 20 }}>
                  {[
                    { label: "MITRE ATT&CK ID", value: attackResult.mitre },
                    { label: "Description", value: attackResult.desc },
                    { label: "Severity", value: attackResult.severity?.toUpperCase() },
                    { label: "Remediation", value: attackResult.remediation },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ marginBottom: 14 }}>
                      <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, letterSpacing: 1 }}>{label}</div>
                      <div style={{ fontFamily: theme.fonts.body, fontSize: 13, color: theme.colors.textSecondary, marginTop: 3, lineHeight: 1.5 }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Attack reference table */}
          <Card>
            <ChartTitle color="#14B8A6">Attack Pattern Reference Database</ChartTitle>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: theme.fonts.mono, fontSize: 12 }}>
                <thead>
                  <tr style={{ background: theme.colors.inputBg }}>
                    {["Attack Type", "Severity", "MITRE ID", "Description", "Remediation"].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: theme.colors.textLabel,
                        borderBottom: `1px solid ${theme.colors.cardBorder}`, fontSize: 11, letterSpacing: 1 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(ATTACK_INTEL).filter(([k]) => k !== "normal" && k !== "benign").map(([key, info], i) => (
                    <tr key={key} style={{ borderBottom: `1px solid ${theme.colors.cardBorder}22`, background: i % 2 === 0 ? theme.colors.rowAlt : "transparent", cursor: "pointer" }}
                      onClick={() => { setAttackInput(key); setAttackResult(info); }}>
                      <td style={{ padding: "8px 14px", color: "#14B8A6", fontWeight: 700, textTransform: "capitalize" }}>{key}</td>
                      <td style={{ padding: "8px 14px" }}><RiskBadge level={info.severity} /></td>
                      <td style={{ padding: "8px 14px", color: theme.colors.accent }}>{info.mitre}</td>
                      <td style={{ padding: "8px 14px", color: theme.colors.textSecondary }}>{info.desc}</td>
                      <td style={{ padding: "8px 14px", color: theme.colors.textMuted, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }}>{info.remediation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* ═══ REFERENCE ═══ */}
      {activeTab === "ref" && (
        <>
          <Card>
            <ChartTitle color="#14B8A6">MITRE ATT&CK Framework — Tactics Overview</ChartTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
              {MITRE_TACTICS.map((tactic, i) => (
                <div key={tactic.id} style={{
                  background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`,
                  borderRadius: 10, padding: 16,
                  borderTop: `3px solid ${["#14B8A6","#00D4FF","#A78BFA","#FF3D57","#FFB800","#00E676"][i % 6]}`
                }}>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, letterSpacing: 1 }}>{tactic.id}</div>
                  <div style={{ fontFamily: theme.fonts.display, fontSize: 13, fontWeight: 700, color: theme.colors.textPrimary, marginTop: 4, marginBottom: 10 }}>{tactic.name}</div>
                  {tactic.techniques.map(t => (
                    <div key={t} style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textSecondary, marginBottom: 4, paddingLeft: 8, borderLeft: `2px solid ${theme.colors.cardBorder}` }}>
                      {t}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <ChartTitle color="#14B8A6">Useful Threat Intelligence Resources</ChartTitle>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { name: "MITRE ATT&CK", url: "https://attack.mitre.org", desc: "Adversary tactics & techniques knowledge base" },
                { name: "AbuseIPDB", url: "https://www.abuseipdb.com", desc: "IP address abuse reporting & lookup" },
                { name: "VirusTotal", url: "https://www.virustotal.com", desc: "File, URL, IP & domain analysis" },
                { name: "Shodan", url: "https://www.shodan.io", desc: "Internet-connected device search engine" },
                { name: "AlienVault OTX", url: "https://otx.alienvault.com", desc: "Open threat exchange & indicators" },
                { name: "CVE Database", url: "https://cve.mitre.org", desc: "Common vulnerabilities & exposures" },
                { name: "NVD NIST", url: "https://nvd.nist.gov", desc: "National vulnerability database" },
                { name: "Cisco Talos", url: "https://talosintelligence.com", desc: "IP/domain reputation lookup" },
                { name: "Feodo Tracker", url: "https://feodotracker.abuse.ch", desc: "Botnet C2 tracker" },
                { name: "URLhaus", url: "https://urlhaus.abuse.ch", desc: "Malware URL database" },
              ].map(r => (
                <a key={r.name} href={r.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <div style={{
                    background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`,
                    borderRadius: 10, padding: "14px 18px",
                    transition: "border-color 0.2s", cursor: "pointer"
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#14B8A688"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = theme.colors.cardBorder}>
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 13, fontWeight: 700, color: "#14B8A6" }}>{r.name} ↗</div>
                    <div style={{ fontFamily: theme.fonts.body, fontSize: 12, color: theme.colors.textSecondary, marginTop: 4 }}>{r.desc}</div>
                  </div>
                </a>
              ))}
            </div>
          </Card>
        </>
      )}

      <div style={{ textAlign: "center", marginTop: 24, fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, paddingBottom: 20 }}>
        NexGen by Hassaan Alrifai · © {new Date().getFullYear()} hassaanalrifai.com
      </div>
    </PageLayout>
  );
}
